// Combined orders feed — the 4 Apps Script endpoints the dashboard's
// pending/history popups read. Kept here (not in a page) so the cart can
// reuse the exact same source and cache; importing from ProductsPage would
// be circular (ProductsPage → Header → CartPopup).

import { API_BASE } from "./api";

export const ORDER_SOURCES = [
  {
    type: "structure",
    url: "https://script.google.com/macros/s/AKfycbzjLNACRiEADmqzbdte-Iiloop_XNOt5D4nVASKlcITbazizWyo2chRotGqOVs5iCxW/exec",
  },
  {
    type: "6mm",
    url: "https://script.google.com/macros/s/AKfycbyj4pVlyIZ8vU4iITHmCPfsMpE8L-7sVIniSlppFlgC5JIVyI2846YFIbDkKKlDs6A/exec",
  },
  {
    type: "binding",
    url: "https://script.google.com/macros/s/AKfycbzXdSGtC_XaArAZmIWbAMXoyrXk7HfUyU3bHms93vnpLF9PjUGq_whwpDyH-MUdQzM/exec",
  },
  {
    type: "rings",
    url: "https://script.google.com/macros/s/AKfycbzCPewSCHAOyB14OBMUgOTfSNvwsvjbkpHKCevFQ9KiOFWPA_2eIq3eoJNuKCcfocjiBw/exec",
  },
];

export const ORDERS_CACHE_KEY = "ordersCombined:v2";

// Loose key matcher — Apps Script exports vary in spacing/case/punctuation
// ("Pending Qty" vs "PendingQty", "GSTIN " vs "GST No").
const normKey = (k) => String(k).toLowerCase().replace(/[\s.()_\-/]/g, "");

export const pickLoose = (r, candidates) => {
  if (!r || typeof r !== "object") return undefined;
  for (const k of candidates) {
    const v = r[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  const wanted = candidates.map(normKey);
  for (const key of Object.keys(r)) {
    if (wanted.includes(normKey(key))) {
      const v = r[key];
      if (v !== undefined && v !== null && v !== "") return v;
    }
  }
  return undefined;
};

/** Rows the dashboard already fetched. Stale is fine — better than nothing. */
export function readOrdersCache() {
  try {
    const raw = localStorage.getItem(ORDERS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.rows) ? parsed.rows : null;
  } catch {
    return null;
  }
}

const toNum = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;

/**
 * "Order Pending (In Kg)" for one GST — total qty still open.
 *
 * Same rule as the dashboard's pending popup: qty > 0, no closure marker
 * (95% Disp Close / Force Close) and not rejected/cancelled.
 */
export function pendingKgForGst(rows, gst) {
  const want = String(gst || "").trim().toUpperCase();
  if (!want || !Array.isArray(rows)) return 0;

  return rows.reduce((sum, r) => {
    const rowGst = String(
      pickLoose(r, ["GSTIN", "GSTIN ", "GST IN", "GST No", "GST NO", "gst"]) ||
        "",
    )
      .trim()
      .toUpperCase();
    if (rowGst !== want) return sum;

    const qty = toNum(
      pickLoose(r, ["Pending", "Pending Qty", "PendingQty", "PENDING"]),
    );
    if (qty <= 0) return sum;

    const closed =
      String(
        pickLoose(r, [
          "95% Disp Close",
          "95 Disp Close",
          "95% Disp. Close",
          "Disp Close",
        ]) || "",
      ).trim() ||
      String(pickLoose(r, ["Force Close", "ForceClose"]) || "").trim();
    if (closed) return sum;

    const status = String(
      pickLoose(r, ["ORDER STATUS", "Status", "STATUS"]) || "",
    ).toLowerCase();
    if (/reject|close|forced|cancel/.test(status)) return sum;

    return sum + qty;
  }, 0);
}

/* The dealer's orders from all product order sheets, filtered by GST on the
   backend (/api/sheets/dealer-orders). The full sheets are too big for the
   browser (Structure alone is ~8 MB), so never download them here. Rows are
   tagged __type: structure | 6mm | binding | rings. Cached 60 s in memory. */
const DEALER_ORDERS_TTL_MS = 60 * 1000;
const _dealerOrders = new Map(); // gstKey → { at, rows, promise }

// view = "history" → Order History straight from the order sheets.
export function fetchDealerOrders(gsts, view = "") {
  const key = [...new Set((gsts || []).map((g) => String(g || "").trim().toUpperCase()).filter(Boolean))]
    .sort()
    .join(",");
  if (!key) return Promise.resolve([]);
  const cacheKey = `${view}|${key}`;
  const hit = _dealerOrders.get(cacheKey);
  if (hit?.rows && Date.now() - hit.at < DEALER_ORDERS_TTL_MS) return Promise.resolve(hit.rows);
  if (hit?.promise) return hit.promise;
  const promise = fetch(`${API_BASE}/api/sheets/dealer-orders?gst=${encodeURIComponent(key)}${view ? `&view=${view}` : ""}`, {
    cache: "no-store",
  })
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    })
    .then((d) => {
      const rows = Array.isArray(d) ? d : [];
      _dealerOrders.set(cacheKey, { at: Date.now(), rows });
      return rows;
    })
    .catch((e) => {
      _dealerOrders.delete(cacheKey);
      if (hit?.rows) return hit.rows; // keep the last good copy
      throw e;
    });
  _dealerOrders.set(cacheKey, { ...(hit || {}), promise });
  return promise;
}

/** Pending kg for a GST (cart). */
export async function getPendingKg(gst) {
  if (!gst) return 0;
  try {
    return pendingKgForGst(await fetchDealerOrders([gst]), gst);
  } catch {
    const cached = readOrdersCache();
    return cached && cached.length ? pendingKgForGst(cached, gst) : 0;
  }
}
